import { useEffect, useMemo, useRef, useState } from 'react';
import CohortDefinition from '../../components/characteristic/CohortDefinition';
import CohortFleetList from '../../components/characteristic/CohortFleetList';
import CohortAgeDistributionChart from '../../components/characteristic/CohortAgeDistributionChart';
import CohortSizeDistributionChart from '../../components/characteristic/CohortSizeDistributionChart';
import { ICohortVessel } from '../../cohortTypes';
import { formatValueAsString } from '../../../../../utils/helpers';
import ExpandableContainer from '../../../../../shared/components/expandable-container/ExpandableContainer';

export interface ICohortCharacteristicsProps {
	vessel: ICohortVessel;
	cohortVessels: ICohortVessel[] | [];
	minSize: number;
	maxSize: number;
	rangeSize: number;
	minAge: number;
	maxAge: number;
}

const CohortCharacteristics: React.FC<ICohortCharacteristicsProps> = ({ ...props }) => {
	const [filteredData, setFilteredData] = useState<ICohortVessel[]>(props.cohortVessels);

	// If the cohort has only one vessel, use its characteristics for the cohort definition. Otherwise, use the selected vessel's characteristics.
	const vesselInfo = props.cohortVessels.length === 1 ? props.cohortVessels[0] : props.vessel;

	useEffect(() => {
		setFilteredData(props.cohortVessels);
	}, [props.cohortVessels]);

	const sizeGroupedDataWithCount = useMemo(() => {
		const dataList = filteredData || [];

		const minSize = props.minSize;
		const maxSize = props.maxSize;
		const rangeSize = props.rangeSize;

		if (minSize === maxSize) {
			const label = formatValueAsString(minSize);
			return dataList.length > 0 ? [{ size: label, imoCount: dataList.length }] : [];
		}

		// Create ranges based on the rangeSize
		const ranges = [];
		for (let start = minSize; start < maxSize; start += rangeSize) {
			ranges.push({ min: start, max: start + rangeSize });
		}

		// Group data by these dynamic ranges
		const groupedData = ranges.map(({ min, max }) => {
			const vesselsInRange = dataList.filter((item) => item.size >= min && item.size < max);

			return {
				size: `${formatValueAsString(min)}-${formatValueAsString(max)}`,
				imoCount: vesselsInRange.length,
			};
		});

		return groupedData.filter((v) => v.imoCount > 0);
	}, [filteredData, props.maxSize, props.minSize, props.rangeSize]);

	const ageGroupedDataWithCount = useMemo(() => {
		const dataList = filteredData || [];

		const groupedData = dataList.reduce(
			(acc, item: ICohortVessel) => {
				const { age, imoNumber } = item;

				if (!acc[age]) {
					acc[age] = {
						age,
						imoList: [],
					};
				}

				acc[age].imoList.push(imoNumber);

				return acc;
			},
			{} as Record<number, { age: number; imoList: number[] }>,
		);

		// Convert groupedData to an array with imo count for each size
		return Object.values(groupedData).map(({ age, imoList }) => ({
			age,
			imoCount: imoList.length,
		}));
	}, [filteredData]);

	const ref = useRef<HTMLDivElement>(null);

	const [panelOffset, setPanelOffset] = useState<{ top: number; left: number }>();

	useEffect(() => {
		const updatePanelSize = () => {
			if (ref.current) {
				const { offsetTop, offsetLeft } = ref.current;
				setPanelOffset({ top: offsetTop, left: offsetLeft });
			}
		};

		// Initial measurement
		updatePanelSize();

		// Update on window resize
		window.addEventListener('resize', updatePanelSize);
		return () => {
			window.removeEventListener('resize', updatePanelSize);
		};
	}, []);

	// const handleFilteredDataChange = (filteredData: ICohortVessel[]) => {
	// 	setFilteredData(filteredData);
	// };

	return (
		<div ref={ref} className="flex flex-col gap-4 w-full">
			<div className="flex flex-none">
				<CohortDefinition
					title="Cohort"
					minSize={props.minSize}
					maxSize={props.maxSize}
					shipsNumber={props.cohortVessels.length}
					size={vesselInfo.size}
					sizeUnit={vesselInfo.sizeUnit}
					age={vesselInfo.age}
					minAge={props.minAge}
					maxAge={props.maxAge}
					isCohort={true}
				/>
			</div>
			<div className="flex flex-col gap-4 basis-1/2">
				<div className="flex flex-row gap-4 h-full w-full">
					<div className="flex flex-col w-full h-full border border-msi-darkgrey-color-300 rounded">
						<ExpandableContainer title="Cohort Size Distribution" panelOffsets={panelOffset}>
							<CohortSizeDistributionChart data={sizeGroupedDataWithCount} sizeUnit={vesselInfo.sizeUnit} />
						</ExpandableContainer>
					</div>
					<div className="flex flex-col w-full h-full border border-msi-darkgrey-color-300 rounded">
						<ExpandableContainer title="Cohort Age Distribution" panelOffsets={panelOffset}>
							<CohortAgeDistributionChart data={ageGroupedDataWithCount} />
						</ExpandableContainer>
					</div>
				</div>
			</div>

			<div className="flex flex-col basis-1/2 border border-msi-darkgrey-color-300 rounded p-1">
				<ExpandableContainer title="Cohort Fleet List" panelOffsets={panelOffset} expandable={props.cohortVessels && props.cohortVessels.length <= 1000}>
					{props.cohortVessels && props.cohortVessels.length <= 1000 ? (
						<CohortFleetList cohortVessels={props.cohortVessels} />
					) : (
						<div className="flex items-center justify-center h-full">
							<p className="text-msi-darkgrey-color-500">This list will populate if the cohort fleet is less than 1000 ships</p>
						</div>
					)}
				</ExpandableContainer>
			</div>
		</div>
	);
};
export default CohortCharacteristics;
